import React, { useState, useRef } from 'react';
import { FaLink, FaRegCopy } from 'react-icons/fa';
import { X } from 'lucide-react';
import { FaInstagram, FaTiktok, FaYoutube, FaVk } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import type { TrackPermissions } from '../../types/track';

interface FileTileProps {
  file: {
    name: string;
    url: string;
    fileId?: string;
    socialLinks?: any;
  };
  icon: React.ReactNode;
  onRemove?: () => void;
  showSocialIcons?: boolean;
  onSocialClick?: (type: string) => void;
  removable?: boolean;
  variant?: 'video' | 'footage';
  permissions?: TrackPermissions; // Permissions for edit vs view-only mode
}

const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 768;

const FileTile: React.FC<FileTileProps> = ({ file, icon, onRemove, showSocialIcons, onSocialClick, removable = true, variant = 'video', permissions }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [activeSocialType, setActiveSocialType] = useState<string>('');
  const tileRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Tooltip position logic (now for file name only)
  let tooltipStyle: React.CSSProperties = { display: 'none' };
  if (showTooltip && nameRef.current) {
    const rect = nameRef.current.getBoundingClientRect();
    tooltipStyle = {
      position: 'fixed',
      top: rect.top - 32,
      left: rect.left + rect.width / 2,
      transform: 'translateX(-50%)',
      zIndex: 9999,
      background: 'white',
      color: '#111',
      border: '1px solid #d1d5db',
      borderRadius: 6,
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      padding: '6px 12px',
      fontSize: '0.8125rem',
      whiteSpace: 'pre-wrap',
      pointerEvents: 'none',
      maxWidth: 260,
    };
  }

  // Helper: check if any social link exists
  const hasAnySocialLink = file.socialLinks && (
    file.socialLinks.instagramReels || file.socialLinks.tiktok || file.socialLinks.youtubeShorts || file.socialLinks.vkClips
  );

  return (
    <>
      <div
        ref={tileRef}
        data-testid={`file-tile-${file.fileId || file.name}`}
        className={`rounded-lg border p-2 flex flex-col items-center w-full sm:min-w-[90px] sm:max-w-[110px] transition relative group cursor-pointer !bg-[var(--color-surface)] !border-[var(--color-border)] ${variant === 'footage' ? 'py-2 min-h-[90px]' : 'min-h-[110px]'}`}
        style={{ 
          ...(variant === 'footage' ? { textDecoration: 'none', minHeight: 90, paddingTop: 8, paddingBottom: 8 } : { minHeight: 110, textDecoration: 'none' })
        }}
        title={isMobile() ? undefined : file.name}
        onClick={e => {
          // Mobile click handling removed - simplified UX
        }}
      >
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-1 right-1"
          style={{ color: 'var(--color-muted)' }}
          onMouseEnter={(e) => e.target.style.color = 'var(--info-blue-text)'}
          onMouseLeave={(e) => e.target.style.color = 'var(--color-muted)'}
                      title={t('fileTile.openFile')}
          onClick={e => e.stopPropagation()}
        >
          <FaLink size={16} />
        </a>
        {removable && (
          <button
            className="absolute top-1 left-1 w-6 h-6 flex items-center justify-center rounded-full border transition"
            style={{ 
              color: 'var(--status-live-text)',
              backgroundColor: 'var(--color-surface)', 
              borderColor: 'var(--color-border)' 
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--status-live-bg)';
              e.target.style.color = 'var(--status-live-text)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-surface)';
              e.target.style.color = 'var(--status-live-text)';
            }}
            title={t('fileTile.removeFile')}
            onClick={e => {
              e.preventDefault();
              if (onRemove) onRemove();
            }}
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <div className="mb-1 mt-3 relative">{icon}</div>
        <div
          ref={nameRef}
          className="text-[11px] font-medium truncate w-20 text-center mb-1"
          style={{ color: 'var(--color-text)' }}
          onMouseEnter={() => { if (!isMobile()) setShowTooltip(true); }}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {file.name}
        </div>
        {/* Only render social icons row if showSocialIcons is true */}
        {showSocialIcons && (
          <div className="flex flex-row justify-center gap-2 mt-2">
            {/* Instagram Reels */}
            <span
              className={file.socialLinks?.instagramReels ? (permissions?.canUploadPromo ? 'cursor-pointer' : 'cursor-pointer') : (permissions?.canUploadPromo ? 'cursor-pointer' : 'cursor-not-allowed')}
              style={{
                color: file.socialLinks?.instagramReels ? '#ec4899' : 'var(--color-muted)'
              }}
              title={file.socialLinks?.instagramReels ? (permissions?.canUploadPromo ? t('social.manageInstagramReelsLink') : t('social.openInstagramReelsLink')) : (permissions?.canUploadPromo ? t('social.addInstagramReelsLink') : t('social.noInstagramReelsLink'))}
              onClick={file.socialLinks?.instagramReels ? (e => {
                e.stopPropagation();
                if (permissions?.canUploadPromo) {
                  setActiveSocialType('instagramReels');
                  setShowSocialModal(true);
                } else {
                  // In view-only mode, open link directly
                  const link = file.socialLinks.instagramReels;
                  try {
                    const url = link.startsWith('http') ? link : `https://${link}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    window.open(link, '_blank');
                  }
                }
              }) : permissions?.canUploadPromo && onSocialClick ? (e => {
                e.stopPropagation();
                setActiveSocialType('instagramReels');
                setShowSocialModal(true);
              }) : undefined}
            >
              <FaInstagram size={18} />
            </span>
            {/* TikTok */}
            <span
              className={file.socialLinks?.tiktok ? (permissions?.canUploadPromo ? 'cursor-pointer' : 'cursor-pointer') : (permissions?.canUploadPromo ? 'cursor-pointer' : 'cursor-not-allowed')}
              style={{
                color: file.socialLinks?.tiktok ? '#000000' : 'var(--color-muted)'
              }}
              title={file.socialLinks?.tiktok ? (permissions?.canUploadPromo ? t('social.manageTikTokLink') : t('social.openTikTokLink')) : (permissions?.canUploadPromo ? t('social.addTikTokLink') : t('social.noTikTokLink'))}
              onClick={file.socialLinks?.tiktok ? (e => {
                e.stopPropagation();
                if (permissions?.canUploadPromo) {
                  setActiveSocialType('tiktok');
                  setShowSocialModal(true);
                } else {
                  // In view-only mode, open link directly
                  const link = file.socialLinks.tiktok;
                  try {
                    const url = link.startsWith('http') ? link : `https://${link}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    window.open(link, '_blank');
                  }
                }
              }) : permissions?.canUploadPromo && onSocialClick ? (e => {
                e.stopPropagation();
                setActiveSocialType('tiktok');
                setShowSocialModal(true);
              }) : undefined}
            >
              <FaTiktok size={18} />
            </span>
            {/* YouTube Shorts */}
            <span
              className={file.socialLinks?.youtubeShorts ? (permissions?.canUploadPromo ? 'cursor-pointer' : 'cursor-pointer') : (permissions?.canUploadPromo ? 'cursor-pointer' : 'cursor-not-allowed')}
              style={{
                color: file.socialLinks?.youtubeShorts ? '#ef4444' : 'var(--color-muted)'
              }}
              title={file.socialLinks?.youtubeShorts ? (permissions?.canUploadPromo ? t('social.manageYouTubeShortsLink') : t('social.openYouTubeShortsLink')) : (permissions?.canUploadPromo ? t('social.addYouTubeShortsLink') : t('social.noYouTubeShortsLink'))}
              onClick={file.socialLinks?.youtubeShorts ? (e => {
                e.stopPropagation();
                if (permissions?.canUploadPromo) {
                  setActiveSocialType('youtubeShorts');
                  setShowSocialModal(true);
                } else {
                  // In view-only mode, open link directly
                  const link = file.socialLinks.youtubeShorts;
                  try {
                    const url = link.startsWith('http') ? link : `https://${link}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    window.open(link, '_blank');
                  }
                }
              }) : permissions?.canUploadPromo && onSocialClick ? (e => {
                e.stopPropagation();
                setActiveSocialType('youtubeShorts');
                setShowSocialModal(true);
              }) : undefined}
            >
              <FaYoutube size={18} />
            </span>
            {/* VK Clips */}
            <span
              className={file.socialLinks?.vkClips ? (permissions?.canUploadPromo ? 'cursor-pointer' : 'cursor-pointer') : (permissions?.canUploadPromo ? 'cursor-pointer' : 'cursor-not-allowed')}
              style={{
                color: file.socialLinks?.vkClips ? '#0077ff' : 'var(--color-muted)'
              }}
              title={file.socialLinks?.vkClips ? (permissions?.canUploadPromo ? t('social.manageVKClipsLink') : t('social.openVKClipsLink')) : (permissions?.canUploadPromo ? t('social.addVKClipsLink') : t('social.noVKClipsLink'))}
              onClick={file.socialLinks?.vkClips ? (e => {
                e.stopPropagation();
                if (permissions?.canUploadPromo) {
                  setActiveSocialType('vkClips');
                  setShowSocialModal(true);
                } else {
                  // In view-only mode, open link directly
                  const link = file.socialLinks.vkClips;
                  try {
                    const url = link.startsWith('http') ? link : `https://${link}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    window.open(link, '_blank');
                  }
                }
              }) : permissions?.canUploadPromo && onSocialClick ? (e => {
                e.stopPropagation();
                setActiveSocialType('vkClips');
                setShowSocialModal(true);
              }) : undefined}
            >
              <FaVk size={18} />
            </span>
          </div>
        )}
      </div>
      {/* Desktop tooltip (outside tile, for file name only) */}
      {!isMobile() && showTooltip && (
        <div style={tooltipStyle}>{file.name}</div>
      )}

      {/* Social Links Modal */}
      {showSocialModal && activeSocialType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={() => setShowSocialModal(false)}>
          <div className="rounded-lg shadow-lg p-6 max-w-xs w-full relative border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }} onClick={e => e.stopPropagation()}>
            <div className="flex flex-col items-center">
              <div className="mb-4">
                {activeSocialType === 'instagramReels' && <FaInstagram size={32} style={{ color: '#ec4899' }} />}
                {activeSocialType === 'tiktok' && <FaTiktok size={32} style={{ color: '#000000' }} />}
                {activeSocialType === 'youtubeShorts' && <FaYoutube size={32} style={{ color: '#ef4444' }} />}
                {activeSocialType === 'vkClips' && <FaVk size={32} style={{ color: '#0077ff' }} />}
              </div>
              
              <div className="font-semibold text-center mb-4" style={{ color: 'var(--color-text)' }}>
                {activeSocialType.replace(/([A-Z])/g, ' $1').trim()}
              </div>
              
              {file.socialLinks?.[activeSocialType] ? (
                // Link exists - show open, edit, remove options
                <>
                  <button
                    className="mb-3 w-full px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 flex items-center justify-center gap-2"
                    onClick={() => {
                      const link = file.socialLinks?.[activeSocialType];
                      if (link) {
                        try {
                          // Ensure the link is properly formatted and opens externally
                          const url = link.startsWith('http') ? link : `https://${link}`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch (error) {
                          // Fallback: try to open as is
                          window.open(link, '_blank');
                        }
                      }
                      setShowSocialModal(false);
                    }}
                  >
                    <FaLink /> Open Link
                  </button>
                  
                  <button
                    className="mb-3 w-full px-4 py-2 rounded text-sm flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                    onClick={() => {
                      if (onSocialClick) {
                        onSocialClick(activeSocialType);
                      }
                      setShowSocialModal(false);
                    }}
                  >
                    ✏️ Edit Link
                  </button>
                  
                  <button
                    className="w-full px-4 py-2 rounded bg-red-500 text-white text-sm hover:bg-red-600 flex items-center justify-center gap-2"
                    onClick={() => {
                      if (onSocialClick) {
                        onSocialClick(`remove_${activeSocialType}`);
                      }
                      setShowSocialModal(false);
                    }}
                  >
                    🗑️ Remove Link
                  </button>
                </>
              ) : (
                // No link - show add option
                <>
                  <button
                    className="w-full px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 flex items-center justify-center gap-2"
                    onClick={() => {
                      if (onSocialClick) {
                        onSocialClick(activeSocialType);
                      }
                      setShowSocialModal(false);
                    }}
                  >
                    ➕ Add Link
                  </button>
                </>
              )}
            </div>
            <button 
              className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center" 
              style={{ color: 'var(--color-muted)' }}
              onMouseEnter={(e) => e.target.style.color = 'var(--color-text)'}
              onMouseLeave={(e) => e.target.style.color = 'var(--color-muted)'}
              onClick={() => setShowSocialModal(false)}
              aria-label="Close"
            >&times;</button>
          </div>
        </div>
      )}
    </>
  );
};

export default FileTile; 